import React, { useEffect, useState } from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import { createUseStyles } from 'react-jss';
import { Button, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import lmsIntegrationAgent from '../../services/api/lmsIntegrationAgent';
import { ELMSType, IIntegrationItem } from '../../services/models/LMSLinkModel';
import SnackbarUtils from '../../components/snackbarUtils/SnackbarUtilsConfigurator';

interface IntegrationComponentProps {
  ownerID: number;
  loadingOwnerID: boolean;
}

export function WPLogoIcon(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  return <img src='/img/LMSProLogo.svg' alt='norsk interaktiv LMS Pro logo' height={'100%'} {...props} />;
}

export function NiasLogoIcon(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  return <img src='/img/logoNIAS.svg' alt='norsk interaktiv logo' height={'100%'} {...props} />;
}

export function SynthesiaLogoIcon(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  return <img src='/img/brand/external/synthesia-small.png' alt='synthesia logo' {...props} />;
}

export default function ActiveIntegrations({ ownerID, loadingOwnerID: _loadingOwnerID }: IntegrationComponentProps) {
  const theme = useTheme();

  const [integrations, setIntegrations] = useState<IIntegrationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch integrations when component mounts
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response: any = await lmsIntegrationAgent.WPLink.getWPLinks(ownerID);
        const items: any[] = response && response.items ? response.items : response;
        const mapped: IIntegrationItem[] = (items || []).map((item: any) => ({
          customerId: item?.lmsCustomerId ?? item?.customerId,
          url: item?.lmsCustomer ?? item?.url,
          lmsTypeId: item?.lmsTypeId,
          ownerId: item?.ownerId,
        }));

        setIntegrations(mapped);
      } catch (error: any) {
        console.error('Error fetching integrations:', error);
        setError('Failed to load integrations');
        SnackbarUtils.error('Failed to load integrations');
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [ownerID]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<IIntegrationItem | null>(null);

  const handleDeleteClick = (integration: IIntegrationItem) => {
    setIntegrationToDelete(integration);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!integrationToDelete) return;

    setDeleting(true);
    try {
      await lmsIntegrationAgent.CourseLMSLink.deleteLMSLink(integrationToDelete.customerId, ownerID);

      // Remove from local state on successful deletion
      setIntegrations((prev) => prev.filter((integration) => integration.customerId !== integrationToDelete.customerId));

      SnackbarUtils.success('Integration deleted successfully');
    } catch (error: any) {
      console.error('Error deleting integration:', error);
      SnackbarUtils.error('Failed to delete integration');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setIntegrationToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setIntegrationToDelete(null);
  };

  const useStyles = createUseStyles((theme: Theme) => ({
    integrationItem: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.border.main}`,
      borderRadius: theme.spacing(1),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 48,
      height: 48,
      marginRight: theme.spacing(2),
      '& svg': {
        width: 32,
        height: 32,
      },
      '& img': {
        height: 32,
        width: 'auto',
        maxHeight: 32,
      },
    },
    integrationInfo: {
      flex: 1,
    },
    integrationUrl: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    integrationType: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      textTransform: 'capitalize',
    },
    actionButtons: {
      marginLeft: 'auto',
      display: 'flex',
      gap: theme.spacing(1),
    },
    goToSiteButton: {
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.light + '20',
      },
    },
    deleteButton: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: theme.palette.error.light + '20',
      },
    },
  }));

  const classes = useStyles({ theme });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', flexDirection: 'column', gap: theme.spacing(4) }}>
      <div style={{ overflow: 'auto', background: theme.palette.bgTwo.main, display: 'flex', width: '100%' }}>
        <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', padding: theme.spacing(2), gap: theme.spacing(2) }}>
          <div>
            <Typography variant='h6' gutterBottom>
              Active Integrations
            </Typography>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: theme.spacing(4) }}>
                <CircularProgress size={40} />
                <Typography variant='body1' style={{ marginLeft: theme.spacing(2) }}>
                  Loading integrations...
                </Typography>
              </div>
            ) : error ? (
              <Typography variant='body1' color='error'>
                {error}
              </Typography>
            ) : integrations.length > 0 ? (
              integrations.map((integration) => (
                <div key={integration.customerId} className={classes.integrationItem}>
                  <div className={classes.iconContainer}>
                    {integration.lmsTypeId === ELMSType.WordPress && <WPLogoIcon />}
                    {integration.lmsTypeId === ELMSType.Mentorkit_LMS && <NiasLogoIcon />}
                    {integration.lmsTypeId === ELMSType.Synthesia && <SynthesiaLogoIcon />}
                  </div>
                  <div className={classes.integrationInfo}>
                    <Typography className={classes.integrationUrl}>{integration.url}</Typography>
                    <Typography className={classes.integrationType}>{ELMSType[integration.lmsTypeId].replaceAll('_', ' ')}</Typography>
                  </div>
                  <div className={classes.actionButtons}>
                    {integration.lmsTypeId === ELMSType.WordPress && (
                      <IconButton
                        className={classes.goToSiteButton}
                        component='a'
                        href={`${integration.url}/wp-admin/admin.php?page=mentorkit`}
                        target='_blank'
                        rel='noopener noreferrer'
                        size='small'
                        title='Go to SCORM Player Uploader'
                      >
                        <LaunchIcon />
                      </IconButton>
                    )}

                    <IconButton className={classes.deleteButton} onClick={() => handleDeleteClick(integration)} size='small' title='Delete integration'>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
              ))
            ) : (
              <Typography variant='body1' color='textSecondary'>
                No active integrations found.
              </Typography>
            )}
          </div>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onClose={deleting ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Integration</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the integration with {integrationToDelete?.url}? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color='error' variant='contained' disabled={deleting}>
            {deleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
